import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Mail, Clock } from "lucide-react";

const EMAILS = [
  {
    id: "email-0",
    day: "Day 0 · Immediate",
    subject_primary: "Your 12-Month I GET TO Calendar is here, dear one.",
    subject_alts: [
      "365 I GET TO prompts · yours now 💛",
      "As promised — the calendar + weekly walk",
    ],
    body: `Dear one,

Here's your free 12-Month I GET TO Calendar — the visual companion to our walk together.

[BUTTON: Download the Calendar — all 13 pages →]
Link to: https://mindbodyspirithygienetools.com/tap-in-calendar

Print it. Put it where your eyes land. The fridge. Your desk. The inside of the bathroom cabinet.

Each week, I'll send you one note — a tiny reframe, a piece of the Tap-In HYGIENE, a real tool. You check off the week. We walk.

If you have 9 minutes today, listen to the audio essay that started all of this:

[BUTTON: The HYGIENE That Reclaimed My Life →]
Link to: https://mindbodyspirithygienetools.com/blog/the-tap-in

Welcome home.

— Temple 💛

P.S. Reply and tell me where your calendar is going to live. I read every reply.`,
  },
  {
    id: "email-1",
    day: "Day 2",
    subject_primary: "How was your first morning, dear one?",
    subject_alts: [
      "Even 30 seconds counts",
      "A whisper of a practice",
    ],
    body: `Dear one,

Did you try Tapping-In yesterday?

Even 30 seconds counts.
Even "I forgot" counts — today is also a HYGIENE day.

The door is always open. You don't graduate from it. You live inside it.

This week, pick ONE thing you've been dreading and reframe it:

"I have to go to work."
→ "I GET TO go to work because I have a body that carries me."

Three words. Same task. Different relationship to your life.

That is HYGIENE.

— Temple 💛`,
  },
  {
    id: "email-2",
    day: "Day 7",
    subject_primary: "A week in · how's your rhythm?",
    subject_alts: [
      "One full week, dear one",
      "Reply and tell me what's shifting",
    ],
    body: `Dear one,

A full week since you said yes to this walk.

What's changing?

Sometimes the smallest thing — a hand on your chest before your phone, a "thank you" before your feet hit the floor — rewires more than you realize.

If something IS shifting — even a whisper — I want to hear about it. Reply to this email and tell me.

If nothing has shifted yet — that counts too. HYGIENE is cumulative. The first week is the hardest because nothing feels different yet.

Stay. Keep going. I am not going anywhere.

— Temple 💛`,
  },
  {
    id: "email-3",
    day: "Day 30",
    subject_primary: "One more free tool for your pocket",
    subject_alts: [
      "For the moments you can't hear clearly",
      "A gift — the Yes-No-Wait cards",
    ],
    body: `Dear one,

A month in. You showed up. That is so much.

I want to give you one more tool — the Yes-No-Wait Discernment Cards. Not for sale. Ever. A gift.

These are for the OTHER moments.

Not the morning practice. That is Tapped-In — preventive HYGIENE.

This is for the in-the-middle moments. When life demands a decision RIGHT NOW and the emotions are too heavy to hear clearly.

Call on your Divine Team. Pull a card. Surrender the answer to your highest good.

Different tool. Different purpose. Same brand of love.

[BUTTON: Get the free Yes-No-Wait Cards →]
Link to: https://mindbodyspirithygienetools.com/yes-no-wait

Print them. Bless them. Keep them in your wallet.

Welcome home.

— Temple 💛`,
  },
];

const CopyBtn = ({ value, label = "Copy", testId }) => {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <button
      onClick={handle}
      className="inline-flex items-center gap-1.5 bg-[#25a1c2] hover:bg-[#068fa2] text-white text-xs font-bold rounded-full px-4 py-2 transition-all"
      data-testid={testId}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied!" : label}
    </button>
  );
};

export default function EmailDrafts() {
  useEffect(() => {
    document.title = "Email Drafts · Tap-In Welcome Sequence | Temple Franklin";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen pb-16" data-testid="email-drafts-page">
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/social" className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Publish Toolkit
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Email Drafts · Private
          </span>
        </div>
      </header>

      <div className="pt-28 px-6">
        <div className="max-w-5xl mx-auto">
          {/* HERO */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#25a1c2] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
              <Mail className="w-3 h-3" /> Paste into MailerLite
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-4">
              Tap-In Welcome Sequence
            </h1>
            <p className="text-[#475569] max-w-2xl mx-auto">
              4 emails · drafted in your voice. Click "Copy" → paste into MailerLite. Schedule per the "Day" label.
            </p>
          </div>

          {/* MAILERLITE SETUP CHECKLIST */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-10" data-testid="setup-checklist">
            <p className="font-bold text-amber-900 mb-3">🔧 Your MailerLite setup (1 hour, one time):</p>
            <ol className="space-y-2 text-sm text-amber-900 list-decimal list-inside">
              <li>MailerLite → Subscribers → Groups → Create group → Name: <code className="bg-amber-100 px-1.5 py-0.5 rounded">Tap-In · 12-Month Calendar</code></li>
              <li>MailerLite → Forms → Embedded → Create → Link it to the group above</li>
              <li>Copy the Group ID from the URL → send to me → I'll wire it into <code className="bg-amber-100 px-1.5 py-0.5 rounded">/tap-in-calendar</code></li>
              <li>MailerLite → Automation → New → Trigger: <em>"Subscriber joins group Tap-In · 12-Month Calendar"</em></li>
              <li>Add 4 email steps using the drafts below · delays: 0 / 2 days / 5 days / 23 days</li>
              <li>Save & activate the automation</li>
              <li>Subscribe with a test email → confirm Email 0 arrives → you're LIVE</li>
            </ol>
          </div>

          {/* EMAILS */}
          <div className="space-y-6" data-testid="emails-list">
            {EMAILS.map((em, idx) => (
              <div key={em.id} className="bg-white rounded-2xl border border-[#CCFBF1] shadow-sm overflow-hidden" data-testid={em.id}>
                {/* Meta bar */}
                <div className="bg-[#042F2E] px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#5EEAD4] text-[#042F2E] font-serif font-bold text-sm flex items-center justify-center">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold">
                        Email {idx + 1} of 4
                      </p>
                      <p className="font-serif text-lg flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#5EEAD4]" />
                        {em.day}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Subject line */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold">
                        Subject line · primary
                      </p>
                      <CopyBtn value={em.subject_primary} label="Copy Subject" testId={`copy-subject-${em.id}`} />
                    </div>
                    <div className="bg-[#F0F5F4] border border-[#CCFBF1] rounded-xl p-4">
                      <p className="text-[#042F2E] font-bold text-sm">{em.subject_primary}</p>
                      <div className="mt-3 pt-3 border-t border-[#CCFBF1]">
                        <p className="text-[10px] text-[#475569] font-bold uppercase tracking-widest mb-1">Or try:</p>
                        <ul className="space-y-1">
                          {em.subject_alts.map((alt, i) => (
                            <li key={i} className="text-[#475569] text-xs italic">· {alt}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold">
                        Email body
                      </p>
                      <CopyBtn value={em.body} label="Copy Body" testId={`copy-body-${em.id}`} />
                    </div>
                    <div className="bg-[#efeae7] border border-[#CCFBF1] rounded-xl p-5">
                      <pre className="text-[#042F2E] text-sm whitespace-pre-wrap font-sans leading-relaxed" data-testid={`body-${em.id}`}>
                        {em.body}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* DM AUTO-REPLY */}
          <div className="mt-12 bg-gradient-to-br from-[#042F2E] to-[#068fa2] rounded-2xl p-8 text-white" data-testid="dm-autoreply-card">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold mb-3">Bonus · Instagram DM Auto-Reply</p>
            <h2 className="font-serif text-2xl mb-4">Trigger word: <span className="text-[#5EEAD4] italic">HYGIENE</span></h2>
            <p className="text-white/70 text-sm mb-4">
              In Instagram → Business settings → Automated messages → Set keyword trigger. Paste this reply:
            </p>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 mb-4">
              <p className="text-[#CCFBF1] text-sm whitespace-pre-line leading-relaxed">
                {`Dear one 💛

Here's your free 12-Month I GET TO Calendar + my weekly walk-with-me emails.

→ https://mindbodyspirithygienetools.com/tap-in-calendar

Print it. Put it where your eyes land. Welcome home.

— Temple`}
              </p>
            </div>
            <CopyBtn value={`Dear one 💛\n\nHere's your free 12-Month I GET TO Calendar + my weekly walk-with-me emails.\n\n→ https://mindbodyspirithygienetools.com/tap-in-calendar\n\nPrint it. Put it where your eyes land. Welcome home.\n\n— Temple`} label="Copy DM Auto-Reply" testId="copy-dm" />
          </div>

          <p className="text-center text-[10px] text-[#475569] italic mt-10">
            Bookmark · mindbodyspirithygienetools.com/email-drafts
          </p>
        </div>
      </div>
    </div>
  );
}
